import React, { ReactNode } from 'react'
import { Box, Accordion, AccordionDetails } from '@mui/material'
import { PartialAction, Embroidery, Position } from 'src/types'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { EmbroideryFormUpDown } from 'src/Components/Domain/EmbroideryFormUpDown'
import { EmbroideryAccordionSummary } from 'src/Components/Domain/EmbroideryAccordionSummary'
import { EmbroideryContent } from 'src/Components/Domain/EmbroideryContent'
import { useLocale } from 'src/locales/useLocale'
import {
  characterCheckHelper,
  cautionGenerator,
  positionObjectGenerator,
  useAtomsEmbroideriesDispatchGenerator
} from 'src/features/atoms/glove/japan/logic/embroiderySetting'
import { positionChecker } from 'src/logic'
import { embroideryFlagGenerator, generateSubColors, selectablePositionGenerator } from 'src/logic'

const BoxWrapper: React.FC<{ mt?: number; children: ReactNode }> = ({ children, mt = 0 }) => (
  <Box width={'100%'} mt={mt}>
    {children}
  </Box>
)

type Props = {
  embroideries: Embroidery[]
  productNumber: string
  selectedIndex: number
  position: Position
  dispatch: React.Dispatch<PartialAction>
}

export const EmbroiderySetter: React.FC<Props> = ({ embroideries, productNumber, selectedIndex, position, dispatch }) => {
  const { t } = useLocale()
  const { handle } = useAtomsEmbroideriesDispatchGenerator(dispatch, embroideries, t)
  const { isFirst } = positionChecker(position)
  const positionObjects = positionObjectGenerator(productNumber, isFirst, t)
  const isYsr = false // TODO: 要修正 このコンポーネントはATOMSのため、YSRの仕様は考慮しない
  return (
    <TabPanel selectedIndex={selectedIndex} index={2}>
      <EmbroideryFormUpDown {...{ embroideries, positionObjects, dispatch }} />
      {embroideries.map((embroidery, i) => {
        const e = embroideryFlagGenerator(embroidery)
        const c = characterCheckHelper(embroidery, t)
        const shadowColors = generateSubColors(e.disabledShadowColor, t.EMBROIDERY_SHADOW_EDGE_COLOR_OBJECTS)
        const edgeColors = generateSubColors(e.disabledEdgeColor, t.EMBROIDERY_SHADOW_EDGE_COLOR_OBJECTS)
        return (
          <Box key={`${embroidery.id}-${i}`} my={1}>
            <Accordion defaultExpanded>
              <EmbroideryAccordionSummary embroideryIndex={i} />
              <AccordionDetails style={{ flexWrap: 'wrap' }}>
                <BoxWrapper>
                  <SelectCard
                    summary={t.POSITION}
                    selectedLabel={embroidery.position?.label}
                    objects={selectablePositionGenerator(embroideries, positionObjects, [], isYsr, i)} // false部分はYSRでのみ仕様
                    index={i}
                    handleChange={handle.position}
                    disabled={c.existsContent}
                    description={cautionGenerator(c.existsContent, 'position', t)}
                  />
                </BoxWrapper>
                <BoxWrapper>
                  <EmbroideryContent
                    content={embroidery.content}
                    contentMaxLength={c.contentMaxLength}
                    embroideryIndex={i}
                    disabled={e.isUnSelectedPosition}
                    // ユーザがハングルで入力した場合は、ハングル文字は刺繍非対応の旨を示す注意文を表示する （c.contentCaution）
                    caution={c.contentCaution.length > 0 ? c.contentCaution : cautionGenerator(e.isUnSelectedPosition, 'contents', t)}
                    handleContent={handle.content}
                  />
                </BoxWrapper>
                <BoxWrapper mt={2}>
                  <SelectCard
                    summary={t.TYPE_FACE}
                    selectedLabel={embroidery.typeFace?.label}
                    objects={c.typeFaceObjects}
                    index={i}
                    handleChange={handle.typeFace}
                    description={cautionGenerator(true, 'typeFace', t)}
                    disabled={!c.existsContent || c.isKoreaContents}
                  />
                </BoxWrapper>
                <BoxWrapper>
                  <SelectCard
                    summary={t.MAIN_COLOR}
                    selectedLabel={embroidery.color?.label}
                    selectedColor={embroidery.color?.color}
                    objects={t.EMBROIDERY_COLOR_OBJECTS}
                    isError={e.isUnSelectedMainColor}
                    disabled={e.isUnSelectedTypeFace}
                    index={i}
                    handleChange={handle.color}
                  />
                </BoxWrapper>
                <BoxWrapper>
                  <SelectCard
                    summary={t.SHADOW_COLOR}
                    selectedLabel={e.shadowColorLabel}
                    selectedColor={embroidery.shadowColor?.color}
                    objects={shadowColors}
                    isError={embroidery.shadowColor?.value === 'unselected'}
                    defaultExpanded={false}
                    caution={cautionGenerator(e.isUnSelectedMainColor, 'shadowColor', t)}
                    index={i}
                    handleChange={handle.shadowColor}
                  />
                </BoxWrapper>
                <BoxWrapper>
                  <SelectCard
                    summary={t.EDGE_COLOR}
                    selectedLabel={e.edgeColorLabel}
                    selectedColor={embroidery.edgeColor?.color}
                    objects={edgeColors}
                    isError={embroidery.edgeColor?.value === 'unselected'}
                    defaultExpanded={false}
                    caution={cautionGenerator(e.isUnSelectedMainColor, 'edgeColor', t)}
                    index={i}
                    handleChange={handle.edgeColor}
                  />
                </BoxWrapper>
              </AccordionDetails>
            </Accordion>
          </Box>
        )
      })}
    </TabPanel>
  )
}
